import { SxProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { JobState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { ComponentProps } from 'react';

import { Job } from '~hooks/useJob/models';
import { Load, LoadCycle } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import useWorkLock from '~hooks/useWorkLock/useWorkLock';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import LoadCard from '../Cards/LoadCard';

const LOADS_TABLE_LAYOUT: SxProps = {
  alignItems: 'flex-start',
  display: 'grid',
  gap: 0.5,
  gridTemplateColumns: '23px 75px 64px 1fr repeat(3, 1fr) 115px 16px',
};

const getCycleTime = (cycles: LoadCycle[], load: Load) => {
  const cycle = cycles.find((cycle) => cycle.fromLoadId === load.id);

  return cycle?.timeMinutes ?? 0;
};

interface LoadsTableProps {
  cycles: LoadCycle[];
  jobTickets: Ticket[];
  loads: Load[];
  onLoadCardClick: (loadId: string, ticket?: Ticket) => void;
  onRemoveLoadClick: (loadId: string) => void;
  reload: () => void;
  sx?: SxProps;
  isEditing?: boolean;
  readonly?: boolean;
  jobState?: JobState;
  updateLoad: (load: Load) => void;
  disableApproval?: boolean;
  job: Job;
}

export default function LoadsTable({
  cycles,
  jobTickets,
  loads,
  onLoadCardClick,
  onRemoveLoadClick,
  reload,
  sx,
  isEditing = false,
  readonly = false,
  jobState,
  updateLoad,
  disableApproval = false,
  job,
}: LoadsTableProps) {
  const theme = useTheme();
  const { userStore } = useStores();
  const userPermissions = userStore.getPermissions();
  const companyId = job.company?.id ?? getEffectiveUserCompanyId(userStore);

  return (
    <Box sx={sx}>
      <Box
        sx={{
          ...LOADS_TABLE_LAYOUT,
          borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
          pb: 1,
        }}
      >
        <TableHeader sx={{ justifySelf: 'center' }}>{t('loads.number')}</TableHeader>
        <TableHeader>{t('loads.status_and_cycle')}</TableHeader>
        <TableHeader sx={{ justifySelf: 'center' }}>{t('common.preview')}</TableHeader>
        <TableHeader>{t('loads.ticket')}</TableHeader>
        <TableHeader>{t('loads.ticket_time')}</TableHeader>

        <Tooltip title={t('loads.pickup_entry')}>
          <TableHeader>{t('loads.pickup')}</TableHeader>
        </Tooltip>

        <Tooltip title={t('loads.dropoff_entry')}>
          <TableHeader>{t('loads.drop_off')}</TableHeader>
        </Tooltip>

        <TableHeader />
        <TableHeader />
      </Box>

      {loads.map((load, index) => {
        const currentTicket = jobTickets.find(({ loadId }) => loadId === load.id);
        const cycleTime = getCycleTime(cycles, load);
        const loadIsClickable =
          (currentTicket && userPermissions.canEditTicket) ||
          (!currentTicket && userPermissions.canCreateTicket);

        return (
          <LockAwareLoadCard
            key={load.id}
            canEditLoad={userPermissions.canEditLoad}
            companyId={companyId}
            cycleTime={cycleTime}
            index={index}
            isDeletable={userPermissions.canDeleteLoad}
            isLast={index === loads.length - 1}
            load={load}
            onClick={() => onLoadCardClick(load.id, currentTicket)}
            onRemoveLoad={() => onRemoveLoadClick(load.id)}
            reload={reload}
            ticket={currentTicket}
            sx={LOADS_TABLE_LAYOUT}
            isEditing={isEditing}
            readonly={readonly || !loadIsClickable}
            jobState={jobState}
            updateLoad={updateLoad}
            disableApproval={disableApproval}
            isOrderEditable={job.order?.editable}
            equipmentCapacityUom={job.equipmentTypeGrossCapacityUnitOfMeasure}
            jobLoadUom={job.loadUnitOfMeasure}
            jobUom={job.unitOfMeasure}
          />
        );
      })}
    </Box>
  );
}

type LockAwareLoadCardProps = ComponentProps<typeof LoadCard> & {
  canEditLoad: boolean;
};

function LockAwareLoadCard({ canEditLoad, readonly, ...props }: LockAwareLoadCardProps) {
  const { load } = props;
  const { isLocked, reason } = useWorkLock({
    billingApprovedAt: load.billingApprovedAt,
    editable: load.editable,
    fieldApprovedAt: load.fieldApprovedAt,
    hasEditPermission: canEditLoad,
  });

  return <LoadCard {...props} readonly={readonly || isLocked} workLockReason={reason} />;
}

const TableHeader = styled(Typography)<TypographyProps>(({ theme }) => ({
  color: theme.brandV2.colors.treadBlack,
  fontSize: '11px',
  fontWeight: 600,
}));
